import type { TelegramThemeParams, TelegramUser, TelegramWebApp } from './types.js';

/** The Telegram WebApp instance, or `undefined` when running outside Telegram. */
export function getWebApp(): TelegramWebApp | undefined {
  return typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
}

/** True when launched inside Telegram with a non-empty initData payload. */
export function hasTelegram(): boolean {
  const app = getWebApp();
  return Boolean(app && app.initData && app.initData.length > 0);
}

export function getInitData(): string {
  return getWebApp()?.initData ?? '';
}

export function getTelegramUser(): TelegramUser | undefined {
  return getWebApp()?.initDataUnsafe.user;
}

export function getTelegramLanguage(): string | undefined {
  return getWebApp()?.initDataUnsafe.user?.language_code;
}

export function getColorScheme(): 'light' | 'dark' | undefined {
  return getWebApp()?.colorScheme;
}

/** Call once on boot: expand, mark ready and wire theme params into CSS. */
export function initTelegram(onThemeChange?: () => void): void {
  const app = getWebApp();
  if (!app) return;
  try {
    app.ready();
    app.expand();
    app.requestFullscreen?.();
    app.disableVerticalSwipes?.();
  } catch (error) {
    console.warn('Telegram init failed', error);
  }
  applyThemeParams(app.themeParams);
  applySafeAreaInsets(app);
  if (onThemeChange) {
    app.onEvent('themeChanged', () => {
      applyThemeParams(app.themeParams);
      applySafeAreaInsets(app);
      onThemeChange();
    });
  }
  app.onEvent('viewportChanged', () => {
    applySafeAreaInsets(app);
  });
  app.onEvent('fullscreenChanged', () => {
    applySafeAreaInsets(app);
  });
}

function edgeInset(
  insets: { top: number; bottom: number } | undefined,
  edge: 'top' | 'bottom',
): number | undefined {
  if (!insets) return undefined;
  const v = insets[edge];
  return typeof v === 'number' ? Math.max(v, 0) : undefined;
}

function applySafeAreaInsets(app: TelegramWebApp): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  const safeTop =
    edgeInset(app.safeAreaInset, 'top') ?? edgeInset(app.safeAreaInsets, 'top') ?? 0;
  const contentTop =
    edgeInset(app.contentSafeAreaInset, 'top') ??
    edgeInset(app.contentSafeAreaInsets, 'top') ??
    0;

  const safeBottom =
    edgeInset(app.safeAreaInset, 'bottom') ?? edgeInset(app.safeAreaInsets, 'bottom') ?? 0;
  const contentBottom =
    edgeInset(app.contentSafeAreaInset, 'bottom') ??
    edgeInset(app.contentSafeAreaInsets, 'bottom') ??
    0;

  root.style.setProperty('--tg-safe-top', `${safeTop}px`);
  root.style.setProperty('--tg-content-safe-top', `${contentTop}px`);
  root.style.setProperty('--tg-top-inset', `${Math.max(safeTop, contentTop)}px`);
  root.style.setProperty('--tg-bottom-inset', `${Math.max(safeBottom, contentBottom)}px`);
}

const THEME_VAR_MAP: Array<[keyof TelegramThemeParams, string]> = [
  ['bg_color', '--tg-bg'],
  ['secondary_bg_color', '--tg-secondary-bg'],
  ['text_color', '--tg-text'],
  ['hint_color', '--tg-hint'],
  ['button_color', '--tg-button'],
  ['button_text_color', '--tg-button-text'],
];

export function applyThemeParams(params: TelegramThemeParams | undefined): void {
  if (!params || typeof document === 'undefined') return;
  const root = document.documentElement;
  let any = false;
  for (const [key, cssVar] of THEME_VAR_MAP) {
    const value = params[key];
    if (value) {
      root.style.setProperty(cssVar, value);
      any = true;
    }
  }
  if (any) root.setAttribute('data-tg-theme', 'true');
}

/** Sync the native header/background colors with the resolved surface color. */
export function syncChromeColors(): void {
  const app = getWebApp();
  if (!app || typeof document === 'undefined') return;
  const styles = getComputedStyle(document.documentElement);
  const surface = styles.getPropertyValue('--surface').trim();
  if (!surface) return;
  try {
    app.setBackgroundColor(surface);
    app.setHeaderColor(surface);
  } catch {
    // older clients only accept named colors — ignore.
  }
}

/** Open an external link via Telegram when available, else a new browser tab. */
export function openLink(url: string): void {
  const app = getWebApp();
  if (app) {
    if (url.startsWith('https://t.me') || url.startsWith('tg://')) {
      app.openTelegramLink(url);
    } else {
      app.openLink(url);
    }
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function haptic(type: 'light' | 'medium' | 'heavy' = 'light'): void {
  try {
    getWebApp()?.HapticFeedback.impactOccurred(type);
  } catch {
    // haptics unsupported — ignore.
  }
}

export function hapticNotify(type: 'error' | 'success' | 'warning'): void {
  try {
    getWebApp()?.HapticFeedback.notificationOccurred(type);
  } catch {
    // ignore.
  }
}

export function hapticSelection(): void {
  try {
    getWebApp()?.HapticFeedback.selectionChanged();
  } catch {
    // ignore.
  }
}
